import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useCampaigns, useSetCampaignStatus, useSocialMarketing } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { PageLoader, EmptyState } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { WorkspaceChannelsPanel } from './WorkspaceChannels.tsx';

function money(v: number | null, currency: string) {
  if (v == null) return '—';
  try { return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(v); } catch { return `${v} ${currency}`; }
}

function CampaignsTable({ workspaceId, adAccountId, currency, canManage }: { workspaceId: string; adAccountId: string; currency: string; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const campaigns = useCampaigns(orgId, workspaceId, adAccountId);
  const setStatus = useSetCampaignStatus(orgId, workspaceId, adAccountId);
  const toast = useToast();

  if (campaigns.isLoading) return <PageLoader />;
  if (!campaigns.data?.length) return <div className="hint" style={{ padding: 14 }}>Nessuna campagna in questo account pubblicitario.</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--label-3)', fontSize: 12 }}>
            <th style={{ padding: '6px 10px' }}>Campagna</th>
            <th style={{ padding: '6px 10px' }}>Stato</th>
            <th style={{ padding: '6px 10px' }}>Obiettivo</th>
            <th style={{ padding: '6px 10px', textAlign: 'right' }}>Spesa</th>
            <th style={{ padding: '6px 10px', textAlign: 'right' }}>Impression</th>
            <th style={{ padding: '6px 10px', textAlign: 'right' }}>Click</th>
            {canManage && <th style={{ padding: '6px 10px' }} />}
          </tr>
        </thead>
        <tbody>
          {campaigns.data.map((c) => (
            <tr key={c.id} style={{ borderTop: '0.5px solid var(--separator)' }}>
              <td style={{ padding: '8px 10px', fontWeight: 600 }}>{c.name}</td>
              <td style={{ padding: '8px 10px' }}>
                <span className="status-pill" style={{ '--sp': c.status === 'ACTIVE' ? 'var(--green)' : 'var(--label-3)' } as React.CSSProperties}>{c.status}</span>
              </td>
              <td style={{ padding: '8px 10px', color: 'var(--label-2)' }}>{c.objective ?? '—'}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(c.spend, currency)}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.impressions ?? '—'}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.clicks ?? '—'}</td>
              {canManage && (
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate(
                      { campaignId: c.id, status: c.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' },
                      { onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita: verifica i permessi pubblicitari su Meta') },
                    )}>
                    {c.status === 'ACTIVE' ? <><Pause size={13} /> Pausa</> : <><Play size={13} /> Attiva</>}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SocialMarketingView({ workspaceId, canManage }: { workspaceId: string; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const marketing = useSocialMarketing(orgId, workspaceId);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (marketing.data?.adAccounts?.length && !selected) setSelected(marketing.data.adAccounts[0]!.id);
  }, [marketing.data, selected]);

  return (
    <>
      <WorkspaceChannelsPanel workspaceId={workspaceId} canManage={canManage} />
      {marketing.isLoading ? <PageLoader /> : !marketing.data ? (
        <div className="card"><EmptyState tone="neutral" title="Nessun account pubblicitario">
          Collega Facebook/Instagram a questo spazio per vedere qui le campagne pubblicitarie.
        </EmptyState></div>
      ) : marketing.data.adAccounts === null ? (
        <div className="card card-pad">
          <div className="form-error" style={{ background: 'var(--fill)', color: 'var(--label-2)' }}>
            Il collegamento attuale non ha ancora i permessi per leggere gli account pubblicitari (ads_read).
            Usa &quot;Ricollega&quot; nella sezione Contenuti per autorizzare anche la gestione ADV.
          </div>
        </div>
      ) : marketing.data.adAccounts.length === 0 ? (
        <div className="card"><EmptyState tone="neutral" title="Nessun account pubblicitario">
          Il Business Manager collegato non ha ancora account pubblicitari attivi.
        </EmptyState></div>
      ) : (
        <>
          <div className="chips" role="tablist" aria-label="Account pubblicitario" style={{ marginBottom: 12 }}>
            {marketing.data.adAccounts.map((a) => (
              <button key={a.id} type="button" role="tab" className="chip" aria-pressed={selected === a.id} onClick={() => setSelected(a.id)}>
                {a.name}{a.status !== 'ACTIVE' ? ' (disattivo)' : ''}
              </button>
            ))}
          </div>
          {selected && (
            <div className="card card-pad">
              <CampaignsTable workspaceId={workspaceId} adAccountId={selected}
                currency={marketing.data.adAccounts.find((a) => a.id === selected)?.currency ?? 'EUR'} canManage={canManage} />
            </div>
          )}
        </>
      )}
    </>
  );
}
